import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { INRService } from '../../../../shared/services/inr.service';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-assign-patient-to-inr-modal',
    templateUrl: './assign-patient-to-inr-modal.component.html',
    styleUrls: ['./assign-patient-to-inr-modal.component.css'],
    providers: [MessageService]
})
export class AssignPatientToInrModalComponent implements OnInit {
    @Input() display: boolean = false;
    @Output() displayChange = new EventEmitter<boolean>();
    @Output() onPatientAssigned = new EventEmitter<void>();

    form: FormGroup;
    loading: boolean = false;

    bloodTypes = [
        { label: 'A+', value: 'A+' },
        { label: 'A-', value: 'A-' },
        { label: 'B+', value: 'B+' },
        { label: 'B-', value: 'B-' },
        { label: 'AB+', value: 'AB+' },
        { label: 'AB-', value: 'AB-' },
        { label: 'O+', value: 'O+' },
        { label: 'O-', value: 'O-' }
    ];

    constructor(
        private fb: FormBuilder,
        private inrService: INRService,
        private messageService: MessageService
    ) {
        this.form = this.fb.group({
            mrn: ['', Validators.required],
            initialMarevanDose: [null, [Validators.required, Validators.min(0), Validators.max(20)]],
            bloodType: [null], // Not marked as required in image, but usually is? Image says (Required) for others.
            targetInrFrom: [null, [Validators.required, Validators.min(1), Validators.max(20)]],
            targetInrTo: [null, [Validators.required, Validators.min(1), Validators.max(20)]],
            startMonitoringDate: [null, Validators.required],
            nextTestDate: [null, Validators.required],
            internalNotes: ['']
        });
    }

    minDate: Date = new Date();
    minNextTestDate: Date = new Date();

    ngOnInit(): void {
        this.minDate = new Date(); // Start date must be today or future
        this.minNextTestDate = new Date();
        this.minNextTestDate.setDate(this.minDate.getDate() + 1); // Default next test date at least tomorrow

        this.form.get('startMonitoringDate')?.valueChanges.subscribe(date => {
            if (date) {
                const nextDay = new Date(date);
                nextDay.setDate(nextDay.getDate() + 1);
                this.minNextTestDate = nextDay;

                const currentNextDate = this.form.get('nextTestDate')?.value;
                if (currentNextDate && currentNextDate < nextDay) {
                    this.form.patchValue({ nextTestDate: null });
                }
            }
        });
    }

    close() {
        this.display = false;
        this.displayChange.emit(false);
        this.form.reset();
    }

    save() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.loading = true;
        const value = this.form.value;

        const payload = {
            mrn: value.mrn,
            initialMarivanDose: value.initialMarevanDose,
            bloodType: value.bloodType,
            targetInrFrom: value.targetInrFrom,
            targetInrTo: value.targetInrTo,
            startMonitoringDate: this.formatDateForApi(value.startMonitoringDate),
            nextTestDate: this.formatDateForApi(value.nextTestDate),
            notes: value.internalNotes
        };

        this.inrService.assignPatientToINR(payload).subscribe({
            next: () => {
                this.loading = false;
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Patient assigned successfully' });
                this.onPatientAssigned.emit();
                this.close();
            },
            error: (err) => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to assign patient' });
                console.error(err);
            }
        });
    }

    private formatDateForApi(date: Date): string {
        if (!date) return '';
        const offsetDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
        return offsetDate.toISOString().split('T')[0];
    }

    getFormValidationErrors() {
        const errors: any = {};
        Object.keys(this.form.controls).forEach(key => {
            const controlErrors = this.form.get(key)?.errors;
            if (controlErrors) {
                errors[key] = controlErrors;
            }
        });
        return errors;
    }
}
